import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMatches } from "react-router-dom";

import { type HelpAction, HelpActionsContext } from "./HelpActions.context";

type HelpActionsProviderProps = {
  children: ReactNode;
};

export const HelpActionsProvider = ({ children }: HelpActionsProviderProps) => {
  const [helpActions, setHelpActions] = useState<HelpAction[]>([]);
  const [activeHelpAction, setActiveHelpAction] = useState<HelpAction | null>(
    null
  );

  const matches = useMatches();

  useEffect(() => {
    // if there are no help actions, return early
    if (!helpActions) {
      return;
    }

    // find the help action that matches the current route
    const matchingHelpAction = helpActions.find((helpAction) => {
      return helpAction.routeIds.some((routeId) => {
        return matches.some((match) => match.id === routeId);
      });
    });

    // if there is a matching help action, set it as the active help action or set it to null
    if (matchingHelpAction) {
      setActiveHelpAction(matchingHelpAction);
    } else {
      setActiveHelpAction(null);
    }
  }, [helpActions, matches]);

  const contextValue = useMemo(() => {
    return {
      addHelpAction: (action: HelpAction) => {
        // if the id already exists, remove the existing action first (to avoid duplicates and the bug with reducer pointers)
        const actions =
          helpActions?.filter((helpAction) => helpAction.id !== action.id) ||
          [];

        setHelpActions([...actions, action]);
      },
      activeHelpAction,
      helpActions,
    };
  }, [activeHelpAction, helpActions]);

  return (
    <HelpActionsContext.Provider value={contextValue}>
      {children}
    </HelpActionsContext.Provider>
  );
};
